import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { siteInfo } from '@/mocks/siteInfo';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          body.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9qseonnahb7382over0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        formEl.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请重试';
        setFormError(msg.includes('spam') || msg.includes('form data is spam') ? '提交失败，请重试' : msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const faqItems = [
    {
      q: '黑料社区的爆料内容是否真实可靠？',
      a: '黑料社区致力于提供真实可靠的爆料资讯。我们的内容来源包括社区成员投稿、圈内人士爆料以及专业调查团队的深度挖掘。每一条爆料在发布前都会经过编辑团队的核实和评估，但我们仍建议读者保持独立思考，理性看待每一条信息。',
    },
    {
      q: '如何向黑料社区投稿爆料？',
      a: '您可以通过本站的联系表单、发送邮件至 tips@heiliao.community 或通过官方微信 heiliao_official 向我们投稿。我们承诺对所有爆料人的身份信息严格保密，绝不泄露您的个人信息。',
    },
    {
      q: '黑料社区是否会删除已发布的文章？',
      a: '我们尊重事实和真相。如果已发布的文章被证实存在严重事实错误，我们会第一时间进行更正或下架处理。对于当事人提出合理异议的情况，我们也会认真核实并作出相应处理。',
    },
    {
      q: '如何成为黑料社区的合作伙伴？',
      a: '我们欢迎各类媒体机构、内容创作者和品牌方与我们合作。请通过联系表单或发送邮件至 tips@heiliao.community 与我们取得联系，我们的商务团队会在3个工作日内回复。',
    },
    {
      q: '黑料社区是否有移动端APP？',
      a: '目前黑料社区主要提供Web端服务，移动端APP正在紧锣密鼓地开发中，预计将在今年内上线。您可以通过手机浏览器访问我们的网站，已完美适配移动端浏览体验。',
    },
  ];

  return (
    <>
      <ScrollToTop />
      <SchemaOrg
        type="WebPage"
        data={{
          name: '关于我们 - 黑料社区',
          url: 'https://heiliao.community/about',
          description: '了解黑料社区的品牌故事、用户口碑与联系方式。',
          isPartOf: { '@type': 'WebSite', name: '黑料社区' },
        }}
      />
      <SchemaOrg
        type="FAQPage"
        data={{
          mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.q,
            acceptedAnswer: { '@type': 'Answer', text: item.a },
          })),
        }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-24 md:pt-28 pb-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="mb-6">
              <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
            </div>

            {/* Brand Intro */}
            <section className="mb-16">
              <div className="max-w-3xl">
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 mb-4">
                  关于<span className="text-primary-600">黑料社区</span>
                </h1>
                <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6">
                  {siteInfo.description}
                </p>
                <div className="grid grid-cols-3 gap-6 mt-8">
                  <div className="text-center p-4 rounded-lg bg-background-100">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600">{siteInfo.members}</div>
                    <div className="text-xs text-foreground-500 mt-1">社区成员</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-background-100">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-accent-600">{siteInfo.articlesCount}</div>
                    <div className="text-xs text-foreground-500 mt-1">爆料文章</div>
                  </div>
                  <div className="text-center p-4 rounded-lg bg-background-100">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary-600">{siteInfo.dailyActive}</div>
                    <div className="text-xs text-foreground-500 mt-1">日活用户</div>
                  </div>
                </div>
              </div>
            </section>

            {/* Features */}
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">平台特色</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {siteInfo.features.map((feat, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600 mb-4">
                      <i className={`${feat.icon} text-xl`}></i>
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">{feat.title}</h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{feat.description}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">用户口碑</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {siteInfo.reputation.map((item, i) => (
                  <div key={i} className="p-5 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="flex items-center gap-1 mb-3">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <i
                          key={s}
                          className={`text-sm ${s < item.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                        ></i>
                      ))}
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                      &ldquo;{item.content}&rdquo;
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-foreground-800">{item.name}</span>
                      <span className="text-xs text-foreground-400">{item.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-16">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">常见问题</h2>
              </div>
              <div className="max-w-3xl space-y-3">
                {faqItems.map((item, i) => (
                  <details key={i} className="group border border-background-200/70 rounded-lg overflow-hidden">
                    <summary className="flex items-center justify-between p-5 cursor-pointer hover:bg-background-100 transition-colors font-medium text-foreground-800 text-sm">
                      {item.q}
                      <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-400"></i>
                    </summary>
                    <div className="px-5 pb-5 text-sm text-foreground-600 leading-relaxed">
                      {item.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">联系我们</h2>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                <div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                    如果您有任何爆料线索、合作意向或意见建议，欢迎通过以下方式联系我们，或直接填写右侧表单，我们会在24小时内回复。
                  </p>
                  <div className="space-y-5">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                        <i className="ri-mail-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">电子邮箱</div>
                        <div className="text-sm font-medium text-foreground-800">{siteInfo.contact.email}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                        <i className="ri-wechat-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">微信公众号</div>
                        <div className="text-sm font-medium text-foreground-800">{siteInfo.contact.wechat}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600">
                        <i className="ri-weibo-line text-lg"></i>
                      </div>
                      <div>
                        <div className="text-xs text-foreground-400">官方微博</div>
                        <div className="text-sm font-medium text-foreground-800">{siteInfo.contact.weibo}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <form
                    onSubmit={handleSubmit}
                    data-readdy-form
                    className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-4"
                  >
                    <div>
                      <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        您的称呼 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="about-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的称呼"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        电子邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="about-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>

                    <div>
                      <label htmlFor="about-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <select
                        id="about-subject"
                        name="subject"
                        className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors"
                      >
                        <option value="爆料投稿">爆料投稿</option>
                        <option value="合作咨询">合作咨询</option>
                        <option value="意见反馈">意见反馈</option>
                        <option value="其他">其他</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="about-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您想说的内容（最多500字）"
                      ></textarea>
                      <div className="text-xs text-foreground-400 mt-1 text-right">最多500字</div>
                    </div>

                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="honeypot-field"
                    />

                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-secondary-300 text-background-50 font-semibold rounded-md transition-colors text-sm whitespace-nowrap cursor-pointer"
                    >
                      {formStatus === 'loading' ? '提交中...' : '提交留言'}
                    </button>

                    {formStatus === 'success' && (
                      <p className="text-center text-sm text-green-600 font-medium">
                        提交成功！我们会尽快与您联系。
                      </p>
                    )}
                    {formStatus === 'error' && (
                      <p className="text-center text-sm text-red-500 font-medium">
                        {formError || '提交失败，请重试'}
                      </p>
                    )}
                  </form>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}